import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { OrderingOption } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { FilterExpression, FilterOption, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { PromptRegisterOptions } from '../models/prompt-register-options';
import { PromptsFilterOption } from '../models/prompts-filter-option';
import { GetPrompts, GetPromptsVariables } from '../queries/generated/GetPrompts';
import { getPromptsQuery } from '../queries/prompts.graphql';

@Injectable({ providedIn: 'root' })
export class PromptRegisterApiService {
   constructor(private apiService: LcsApiService) {}

   getPrompts(
      endCursor: string | null,
      filters: PromptsFilterOption[] | null,
      quickFilters: PromptRegisterOptions | null,
      orderingOptions: OrderingOption[] | null
   ): Observable<ExecutionResult<GetPrompts>> {
      if (quickFilters?.includeInactivePrompts && filters) {
         filters = filters.filter((f) => f.field !== 'IsActive');
      }
      const filterBuilderFilterExpression: FilterExpression | null =
         filters && filters.length > 0
            ? {
                 filterOptions: filters.map(this.convertPromptFilterOption.bind(this)),
              }
            : { filterOptions: [] };
      if (quickFilters) {
         if (quickFilters.typeName) {
            filterBuilderFilterExpression.filterOptions.push({
               field: 'TypeId',
               operation: FilterOperations.EqualTo,
               value: quickFilters.typeName.typeName,
            });
         }
         if (quickFilters.searchText) {
            filterBuilderFilterExpression.filterOptions.push({
               field: 'Name',
               operation: FilterOperations.Contains,
               value: quickFilters.searchText.trim(),
            });
         }
      }
      const variables = this.getPromptsVariables(endCursor, filterBuilderFilterExpression, orderingOptions);
      return this.apiService.query<GetPrompts, GetPromptsVariables>({
         query: getPromptsQuery,
         variables,
      });
   }

   private convertPromptFilterOption(option: PromptsFilterOption): FilterOption {
      return {
         field: option.field,
         operation: option.operation,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(option: PromptsFilterOption): FilterPrimitive | FilterPrimitive[] | null {
      if (option.operation === FilterOperations.HasValue) {
         return option.value;
      }
      switch (option.type) {
         case 'prompt':
            return Array.isArray(option.value)
               ? option.value.map((v) => {
                    return v.typeName;
                 })
               : option.value?.typeName || null;
         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return option.value;
      }
   }

   private getPromptsVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null
   ): GetPromptsVariables {
      const variables: GetPromptsVariables = {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: 50,
         orderingOptions,
      };

      return variables;
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters) {
         return serializeFilterExpression({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeFilterExpression({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeFilterExpression(expressions[0]);
      } else {
         return '';
      }
   }
}
